import { useCallback, type SetStateAction, useMemo } from 'react';
import type { GeneralFunction } from '@asherng/common-types';
import useState from '../useStateIfMounted/useStateIfMounted';
import type { UseStateControllerParams, UseStateControllerResponse } from './types';

const useStateController = <T>({
  value: valueProp,
  defaultValue,
  onChange,
  shouldUpdate = (prev, next) => prev !== next
}: UseStateControllerParams<T>): UseStateControllerResponse<T> => {
  const [valueState, setValue] = useState<T>(defaultValue);

  const isControlled = valueProp !== undefined;

  const value = useMemo(
    () => (isControlled ? valueProp : valueState),
    [valueState, valueProp, isControlled]
  );

  const updateValue = useCallback(
    (next: SetStateAction<T>) => {
      const nextValue = (typeof next === 'function' ? (next as GeneralFunction)(value) : next) as T;

      if (!shouldUpdate(value, nextValue)) {
        return;
      }

      if (!isControlled) {
        setValue(nextValue);
      }

      onChange?.(nextValue);
    },
    [value, shouldUpdate, isControlled, onChange]
  );

  return [value, updateValue] as UseStateControllerResponse<T>;
};

export default useStateController;
